import ModalButton from "@/Components/ModalButton"
import { setModalData } from "@/store/globalSlice"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { leaveGame } from "@/utils/leaveGame"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import { useNavigate } from "react-router-dom"
import Modal from "@/Components/Modal"

interface LeaveGameConfirmModalProps {
  /**
   * "waiting-room" is the same prompt in front of a game that has not started
   * yet (the /newgame room). Leaving is the same call either way — the server
   * takes the seat back off this socket — but nothing is lost there, so the
   * copy drops the warning about not getting back in.
   */
  variant?: "game" | "waiting-room"
}

const LeaveGameConfirmModal: React.FC<LeaveGameConfirmModalProps> = ({
  variant = "game",
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const myGame = useAppSelector((state) => state.global.myGame)
  const isWaitingRoom = variant === "waiting-room"

  const handleCancel = () => {
    dispatch(setModalData({ leaveGameConfirm: { visible: false } }))
  }

  const handleConfirm = () => {
    navigate("/lobby")
    leaveGame(myGame, dispatch)
  }

  return (
    <Modal
      data-testid="game.leave-game-confirm-modal"
      // Below 1000px (`mobile:`) index.css halves the root font size, so every
      // rem step lands at half of what it reads as on desktop — `max-w-md` is
      // 224px there, and the panel is one short sentence and two buttons. The
      // px cap below gives the sentence and the two side-by-side buttons the
      // width they need at the phone sizes restated further down.
      className={
        "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[300] w-[90%] md:w-auto max-w-md " +
        "mobile:max-w-[280px]"
      }
      icon={
        <ExclamationTriangleIcon className="h-8 w-8 text-white mobile:h-[18px] mobile:w-[18px]" />
      }
      // Same halving as above: the title and subtitle would otherwise land at
      // 12px and 7px.
      title={
        <span className="mobile:text-[20px]">
          {isWaitingRoom ? "Leave this room?" : "Leave game?"}
        </span>
      }
      subtitle={
        <span className="mobile:text-[12px]">
          {isWaitingRoom
            ? "You'll go back to the lobby"
            : "You may not be able to rejoin"}
        </span>
      }
      contentClassName="flex flex-col gap-6 px-6 md:px-8 pb-8 mobile:gap-[14px] mobile:px-[14px] mobile:pb-[16px]"
    >
      {/* Message */}
      <div className="font-poppins text-base md:text-lg text-center font-medium text-gray-200 mobile:text-[15px] mobile:leading-snug">
        {isWaitingRoom
          ? "Are you sure you want to leave this game room?"
          : "Are you sure you want to leave this game? You may not be able to rejoin."}
      </div>

      {/* Action Buttons */}
      {/* `size="md"` is rem-sized too, so on a phone it comes out at 14px of
          side padding around 8px of label. The px steps below keep the pair on
          one row inside the 280px panel while staying comfortably tappable. */}
      <div className="flex justify-center gap-4 mobile:gap-[12px]">
        <ModalButton
          size="md"
          color="default"
          onClick={handleCancel}
          className="mobile:px-[16px] mobile:py-[10px]"
          data-testid="game.leave-game-confirm-cancel-btn"
        >
          <div className="text-white font-medium font-poppins text-lg mobile:text-[15px]">
            Cancel
          </div>
        </ModalButton>
        <ModalButton
          size="md"
          color="red"
          onClick={handleConfirm}
          className="mobile:px-[16px] mobile:py-[10px]"
          data-testid="game.leave-game-confirm-leave-btn"
        >
          <div className="flex items-center gap-2 mobile:gap-[6px]">
            <i className="text-white fa-solid fa-sign-out-alt fa-lg mobile:text-[14px] shrink-0"></i>
            <div className="text-white font-medium font-poppins text-lg mobile:text-[15px]">
              Leave
            </div>
          </div>
        </ModalButton>
      </div>
    </Modal>
  )
}

export default LeaveGameConfirmModal
